import React, { useState, useEffect, useCallback } from 'react';
import {
  Table, Card, Button, Input, Select, Space, Modal, Form, Tag,
  message, Popconfirm, Switch,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import useAuthStore from '../store/authStore';
import useServerTable from '../hooks/useServerTable';
import { apiErrorMessage } from '../utils/apiError';
import {
  getSchools, getSchoolDistricts, createSchool, updateSchool, deleteSchool,
} from '../api/schools';
import { SCHOOL_TYPE_LABELS } from '../components/common/SchoolSelect';

const TYPE_COLORS = {
  anaokulu: 'magenta',
  ilkokul:  'blue',
  ortaokul: 'geekblue',
  lise:     'purple',
  diger:    'default',
};

/**
 * Okul katalogu yonetimi.
 *
 * Ogrenci formlarindaki okul alani eskiden serbest metindi; 392 ogrenci icin
 * 191 farkli yazim olusmustu. Artik okullar burada tanimlaniyor, ogrenci
 * ekranlarinda yalnizca SECILIYOR.
 *
 * Ekleme/duzenleme schools.manage iznine bagli (sube muduru ve ustu).
 * Ogretmen sayfayi gorebilir ama duzenleyemez - butonlar gizli, asil kisit
 * backend'de (UI'da gizlemek yetki degildir).
 */
const Schools = () => {
  const { user } = useAuthStore();
  const canManage = (user?.permissions || []).includes('schools.manage');

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [districts, setDistricts] = useState([]);

  const [search, setSearch] = useState('');
  const [type, setType] = useState(undefined);
  const [ownership, setOwnership] = useState(undefined);
  const [district, setDistrict] = useState(undefined);
  const [isActive, setIsActive] = useState(undefined);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const table = useServerTable('schools', 20);

  // Filtre state'lerinin HEPSI bagimlilik dizisinde: biri unutulursa o filtre
  // sayfa degisince sessizce sifirlanir (CLAUDE.md'deki stale closure tuzagi).
  const fetchRows = useCallback(async (overrides = {}) => {
    setLoading(true);
    try {
      const { data } = await getSchools({
        ...table.params(overrides),
        search: search.trim() || undefined,
        type,
        ownership,
        district,
        is_active: isActive,
      });
      setRows(data.data || []);
      setTotal(data.total || 0);
    } catch (error) {
      message.error(apiErrorMessage(error, 'Okul listesi yüklenemedi.'));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [table.page, table.pageSize, table.sorter, search, type, ownership, district, isActive]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  useEffect(() => {
    getSchoolDistricts()
      .then((res) => setDistricts(res.data || []))
      .catch(() => setDistricts([]));
  }, []);

  const openCreate = () => {
    setEditing(null);
    form.setFieldsValue({
      name: '', type: 'ilkokul', ownership: 'resmi',
      district: 'Aliağa', city: 'İzmir', notes: '', is_active: true,
    });
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    form.setFieldsValue({
      name: row.name,
      type: row.type,
      ownership: row.ownership,
      district: row.district,
      city: row.city,
      notes: row.notes,
      is_active: row.is_active,
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      if (editing) {
        await updateSchool(editing.id, values);
        message.success('Okul güncellendi.');
      } else {
        await createSchool(values);
        message.success('Okul eklendi.');
      }
      setModalOpen(false);
      fetchRows();
    } catch (error) {
      message.error(apiErrorMessage(error, 'Okul kaydedilemedi.'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    try {
      await deleteSchool(row.id);
      message.success('Okul silindi.');
      fetchRows();
    } catch (error) {
      // Bagli ogrencisi olan okul silinemez (backend 409 doner) - mesaji
      // oldugu gibi gosteriyoruz, kullanici ne yapmasi gerektigini oradan
      // ogreniyor ("Silmek yerine pasife alın").
      message.error(apiErrorMessage(error, 'Okul silinemedi.'));
    }
  };

  const columns = [
    {
      title: 'Okul Adı',
      dataIndex: 'name',
      key: 'name',
      render: (v, row) => (
        <span style={{ fontWeight: 500, opacity: row.is_active ? 1 : 0.45 }}>{v}</span>
      ),
    },
    {
      title: 'Tür',
      dataIndex: 'type',
      key: 'type',
      width: 120,
      render: (v) => <Tag color={TYPE_COLORS[v] || 'default'}>{SCHOOL_TYPE_LABELS[v] || v}</Tag>,
    },
    {
      title: 'Sahiplik',
      dataIndex: 'ownership',
      key: 'ownership',
      width: 100,
      render: (v) => (v === 'ozel' ? <Tag color="gold">Özel</Tag> : <Tag>Resmî</Tag>),
    },
    { title: 'İlçe', dataIndex: 'district', key: 'district', width: 120 },
    { title: 'İl',   dataIndex: 'city',     key: 'city',     width: 100 },
    {
      title: 'Öğrenci',
      dataIndex: 'students_count',
      key: 'students_count',
      align: 'center',
      width: 100,
      render: (v) => (v > 0 ? <strong>{v}</strong> : <span style={{ color: '#bbb' }}>0</span>),
    },
    {
      title: 'Durum',
      dataIndex: 'is_active',
      key: 'is_active',
      align: 'center',
      width: 100,
      render: (v) => (v ? <Tag color="green">Aktif</Tag> : <Tag>Pasif</Tag>),
    },
    ...(canManage ? [{
      title: 'İşlem',
      key: 'actions',
      width: 110,
      align: 'center',
      render: (_, row) => (
        <Space size="small">
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(row)} />
          <Popconfirm
            title="Bu okul silinsin mi?"
            description={row.students_count > 0
              ? `${row.students_count} öğrenci bağlı — silinemez, pasife alın.`
              : 'Bu işlem geri alınamaz.'}
            okText="Sil"
            cancelText="Vazgeç"
            onConfirm={() => handleDelete(row)}
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    }] : []),
  ];

  return (
    <Card
      title="Okullar"
      extra={canManage && (
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
          Okul Ekle
        </Button>
      )}
    >
      <Space wrap style={{ marginBottom: 16 }}>
        <Input.Search
          placeholder="Okul adında ara..."
          allowClear
          style={{ width: 260 }}
          onSearch={(v) => { setSearch(v); table.resetToFirstPage(); }}
          onChange={(e) => { if (!e.target.value) { setSearch(''); table.resetToFirstPage(); } }}
        />
        <Select
          placeholder="Tür"
          allowClear
          style={{ width: 140 }}
          value={type}
          onChange={(v) => { setType(v); table.resetToFirstPage(); }}
          options={Object.entries(SCHOOL_TYPE_LABELS).map(([value, label]) => ({ value, label }))}
        />
        <Select
          placeholder="Sahiplik"
          allowClear
          style={{ width: 130 }}
          value={ownership}
          onChange={(v) => { setOwnership(v); table.resetToFirstPage(); }}
          options={[{ value: 'resmi', label: 'Resmî' }, { value: 'ozel', label: 'Özel' }]}
        />
        <Select
          placeholder="İlçe"
          allowClear
          showSearch
          style={{ width: 150 }}
          value={district}
          onChange={(v) => { setDistrict(v); table.resetToFirstPage(); }}
          options={districts.map((d) => ({ value: d, label: d }))}
        />
        <Select
          placeholder="Durum"
          allowClear
          style={{ width: 120 }}
          value={isActive}
          onChange={(v) => { setIsActive(v); table.resetToFirstPage(); }}
          options={[{ value: 1, label: 'Aktif' }, { value: 0, label: 'Pasif' }]}
        />
      </Space>

      <Table
        rowKey="id"
        size="small"
        loading={loading}
        dataSource={rows}
        columns={columns.map((c) => ({ ...c, ...table.sortProps(c.key) }))}
        {...table.tableProps(total, { showTotal: (t) => `${t} okul` })}
      />

      <Modal
        title={editing ? 'Okul Düzenle' : 'Okul Ekle'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={handleSave}
        confirmLoading={saving}
        okText="Kaydet"
        cancelText="Vazgeç"
        destroyOnHidden
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="Okul Adı"
            rules={[{ required: true, message: 'Okul adı zorunlu.' }]}
            extra="MEB kayıtlarındaki resmî adı yazın — kısaltma/takma ad girilirse aynı okul iki kez eklenmiş olur."
          >
            <Input maxLength={150} placeholder="Örn. Aliağa Cumhuriyet İlkokulu" />
          </Form.Item>

          <Space style={{ display: 'flex' }} align="start">
            <Form.Item
              name="type"
              label="Tür"
              rules={[{ required: true, message: 'Tür seçin.' }]}
              style={{ flex: 1, minWidth: 160 }}
            >
              <Select options={Object.entries(SCHOOL_TYPE_LABELS).map(([value, label]) => ({ value, label }))} />
            </Form.Item>
            <Form.Item
              name="ownership"
              label="Sahiplik"
              rules={[{ required: true, message: 'Sahiplik seçin.' }]}
              style={{ flex: 1, minWidth: 140 }}
            >
              <Select options={[{ value: 'resmi', label: 'Resmî' }, { value: 'ozel', label: 'Özel' }]} />
            </Form.Item>
          </Space>

          <Space style={{ display: 'flex' }} align="start">
            <Form.Item name="district" label="İlçe" style={{ flex: 1 }}>
              <Input maxLength={100} placeholder="Aliağa" />
            </Form.Item>
            <Form.Item name="city" label="İl" style={{ flex: 1 }}>
              <Input maxLength={100} placeholder="İzmir" />
            </Form.Item>
          </Space>

          <Form.Item name="notes" label="Not">
            <Input.TextArea rows={2} placeholder="İsteğe bağlı" />
          </Form.Item>

          <Form.Item
            name="is_active"
            label="Aktif"
            valuePropName="checked"
            extra="Pasif okul yeni öğrenci seçiminde çıkmaz; mevcut kayıtlar etkilenmez."
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default Schools;
